import {
  AddCircleOutline,
  CalendarToday,
  ChatBubbleOutlineOutlined,
  CheckCircleOutline,
  DeleteOutlineOutlined,
  DescriptionOutlined,
  EditLocationAltOutlined,
  ErrorOutline,
  LocalOfferOutlined,
  HomeOutlined,
  HomeWorkOutlined,
  ReceiptLongOutlined,
  HourglassEmpty,
  InfoOutlined,
  LocationOn,
  RefreshOutlined,
  ReportProblemOutlined,
  SyncOutlined,
  TrendingDown,
  TrendingUp,
  WaterOutlined,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
} from "@mui/material";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import { isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import EnhancedCommentItemRenderer from "../../../_src_shared/comments/EnhancedCommentItemRenderer";
import useAssociationUtils from "../../associations/hooks/useAssociationUtils";
import { useCommentUtilsHook } from "../../../hooks/utils/useCommentUtilsHook";
import { detailSidebarClear } from "../../../redux/reducers/detailSidebarReducer";
import {
  sidebarSetStateValue,
  sidebarTypeToggle,
} from "../../../redux/actions/sidebarActions";
import { loanDrilldownSidebarTypes } from "../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { selectOptionsFeasibilityReportStatus } from "../../../constants/selectOptions/selectOptionsFeasibilityReportStatus";
import { useModelFieldOptions } from "../../../hooks/useModelFieldOptions";
import { useFetchedSelectionsRequestsHook } from "../../../hooks/useFetchedSelectionsRequestsHook";
import {
  isConstructionProduct,
  isInterestReserveProduct,
} from "../../../utils/loan/productType";
import SidebarTabSwitcher from "../../borrowerDetails/shared/SidebarTabSwitcher";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import {
  loanDrilldownSet,
  loanDrilldownSetKey,
} from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import { formatDate as formatDateShared } from "../../../_src_shared/utils/functions/formatDate";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { isDocHidden } from "../../../_src_shared/utils/functions/isDocHidden";
import { formatBoolean } from "../../../utils/booleans/formatBoolean";
import { formatNumber } from "../../../utils/numbers/formatNumber";
import { formatPercent } from "../../../utils/numbers/formatPercent";
import RffFormChanges from "../../finalForm/RffFormChanges";
import LoanEnhancedCommentTextBox from "../../common/Loan/LoanEnhancedCommentTextBox";
import AssignLoanFilesButton from "../../common/Loan/buttons/AssignLoanFilesButton";
import JsonPreview from "../../common/JsonPreview";
import LoanSubjectPropertyFileUploadProcess from "../../processes/LoanSubjectPropertyFileUploadProcess";
import InlineEditableLoanFilesTable from "../../../shared/tables/InlineEditableLoanFilesTable";
import ConfirmationDialog from "../../ui/ConfirmationDialog";
import SearchInput from "../../inputs/SearchInput";
import Flx from "../../layout/Flx";
import TabContent from "../../navigation/TabContent";
import Txt from "../../typography/Txt";
import QuickActionButton from "../../borrowerDetails/components/QuickActionButton";
import SidebarBodyContent from "../components/SidebarBodyContent";
import ContentArea from "../shared/ContentArea";
import PanelCard, { PanelCardCollapseProvider } from "../shared/PanelCard";
import CardCollapseButtons from "../shared/CardCollapseButtons";
import SubTabSwitcher from "../shared/SubTabSwitcher";
import useCardMasonry, {
  SIDEBAR_DEFAULT_WIDTH,
} from "../shared/useCardMasonry";
import PanelRow from "../shared/PanelRow";
import { FIELD_TYPE, fieldPassesSearch } from "../shared/sidebarFieldUtils";
import ResizeableSidebar from "../ResizeableSidebar";
import { PanelIconButton } from "../shared/panelChrome";
import RffTextareaField from "../../finalForm/inputs/RffTextareaField";
import RecordDocCoveragePanel from "../../../screens/LoanDrilldownScreen/shared/RecordDocCoveragePanel";
import { slate } from "../../../theme/colors";
import PropertyAddressCardTab from "./PropertyAddressCardTab";
import PropertyAddressEditor from "./PropertyAddressEditor";
import PropertyUnitsGrid from "./PropertyUnitsGrid";
import PropertyUnitsSummaryTab from "./PropertyUnitsSummaryTab";
import SidebarSectionTitle from "../shared/SidebarSectionTitle";
import RffPendingChangesBanner from "../shared/RffPendingChangesBanner";
import { selectOptionsPropertyType } from "../../../constants/selectOptions/selectOptionsPropertyType";
import { selectOptionsPropertyConditionAtOrigination } from "../../../constants/selectOptions/selectOptionsPropertyConditionAtOrigination";
import { selectOptionsPropertyStatusAtOrigination } from "../../../constants/selectOptions/selectOptionsPropertyStatusAtOrigination";
import { KpiWidget } from "../../data/KpiWidgetGroup";
import PANEL_TOKENS from "../../primitives/panel/panelTokens";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";
import {
  CASH_OUT_REFI,
  LEGACY_DELAYED_PURCHASE,
} from "../../../utils/loans/delayedPurchase";

const TABS = {
  SUMMARY: "Summary",
  ADDRESS: "Address",
  DETAILS: "Information",
  UNITS: "Units",
  FILES: "Documents",
  MARKET_DATA: "Market Data",
  COMMENTS: "Comments",
  JSON: "JSON",
};

// Loan purposes where the property's purchase fields describe the ORIGINAL
// acquisition (same list as the Add Property form). A delayed purchase is a
// cash-out refinance + the delayedPurchase flag now (REQ-106), so it is already
// covered by "Refinance - Cash Out"; the retired purpose string stays for legacy
// loans that still carry it.
const REFI_PURPOSES = [
  "Refinance - No Cash Out",
  CASH_OUT_REFI,
  LEGACY_DELAYED_PURCHASE,
];

const PROPERTY_DETAIL_TABS = {
  ALL: "All",
  DETAILS: "Details",
  VALUATION: "Valuation",
  PURCHASE: "Purchase",
  LISTING: "Listing",
  FLOOD: "Flood",
  COSTS: "Carrying Costs",
  BUDGET: "Budget",
  NOTES: "Notes",
};

// const BODY_BACKGROUND_COLOR = "#f1f5f9"; // bv
const BODY_BACKGROUND_COLOR = "#f5f6fa";
// const BODY_BACKGROUND_COLOR = "#ffffff";
// const BODY_BACKGROUND_COLOR = "#f5f4ed";

const LoanSubjectPropertySidebar = ({ property }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanId = loanDrilldown?._id;
  const { getCommentsAssociatedWithProperty } = useCommentUtilsHook();
  const { getAssociationDocumentsByPropertyId } = useAssociationUtils();
  const { deleteSubjectProperty } = useUnderwritingHook();
  const { getModelSelectOptions } = useFetchedSelectionsRequestsHook();

  // Only the loan model schema is fetched at dashboard bootstrap; the property
  // selects (feasibility report status) read the subjectProperty one, so ask
  // for it once here. Cached in Redux + localStorage, so this is a no-op after
  // the first open.
  const subjectPropertySchema = useSelector(
    (state) => state.fetchedSelections?.["modelSchema:subjectProperty"],
  );
  useEffect(() => {
    if (!subjectPropertySchema) getModelSelectOptions({ objectType: "subjectProperty" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectPropertySchema]);

  const [primaryTab, setPrimaryTab] = useState(TABS.DETAILS);
  const [commentKey, setCommentKey] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Set when something asks for a new unit row (Summary quick action, the
  // Properties-tab card's Add); the Units table consumes it and clears it.
  const [unitAddRequested, setUnitAddRequested] = useState(false);
  const clearUnitAddRequest = useCallback(() => setUnitAddRequested(false), []);

  // The Properties-tab card opens the sidebar straight onto Units — Edit on
  // the tab, Add on the tab with a new row open. `meta.key` is unique per
  // open, so a repeat click re-applies it.
  const openMeta = useSelector((state) => state.detailSidebar?.meta);
  useEffect(() => {
    if (!openMeta?.unitsAction) return;
    setPrimaryTab(TABS.UNITS);
    if (openMeta.unitsAction === "add") setUnitAddRequested(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMeta?.key]);

  const handleConfirmDelete = useCallback(() => {
    if (!loanId || !property?._id) return;
    setDeleting(true);
    deleteSubjectProperty({
      loanId,
      propertyId: property._id,
      onSuccessFn: (updatedLoan) => {
        dispatch(loanDrilldownSet(updatedLoan));
        dispatch(detailSidebarClear());
        setDeleting(false);
        setConfirmDelete(false);
      },
      onFailFn: () => {
        setDeleting(false);
      },
    });
  }, [loanId, property?._id, deleteSubjectProperty, dispatch]);

  const associatedComments = useMemo(() => {
    if (!property?._id) return [];
    return getCommentsAssociatedWithProperty(
      loanDrilldown?.comments,
      property._id,
    );
  }, [
    getCommentsAssociatedWithProperty,
    loanDrilldown?.comments,
    property?._id,
  ]);

  const propertyFiles = useMemo(
    () =>
      (
        getAssociationDocumentsByPropertyId(loanDrilldown, property?._id) || []
      ).filter((f) => !isDocHidden(f)),
    [getAssociationDocumentsByPropertyId, loanDrilldown, property?._id],
  );

  const units = useMemo(() => property?.units || [], [property?.units]);

  const TAB_LIST = useMemo(
    () => [
      // Information leads — it's the landing tab.
      { label: TABS.DETAILS, value: TABS.DETAILS },
      { label: TABS.SUMMARY, value: TABS.SUMMARY },
      { label: TABS.ADDRESS, value: TABS.ADDRESS },
      { label: `${TABS.UNITS} (${size(units)})`, value: TABS.UNITS },
      { label: `${TABS.FILES} (${size(propertyFiles)})`, value: TABS.FILES },
      { label: `${TABS.MARKET_DATA}`, value: TABS.MARKET_DATA },
      {
        label: `${TABS.COMMENTS} (${size(associatedComments)})`,
        value: TABS.COMMENTS,
      },
      { label: TABS.JSON, value: TABS.JSON },
    ],
    [units, propertyFiles, associatedComments],
  );

  const handleClose = () => {
    // Reset to the landing tab so the next property opens where this one did.
    setPrimaryTab(TABS.DETAILS);
    dispatch(detailSidebarClear());
  };

  const handleGoToAddressTab = useCallback(() => {
    setPrimaryTab(TABS.ADDRESS);
  }, []);

  const handleOpenOrderFloodCert = useCallback(() => {
    if (!property?._id) return;
    dispatch(sidebarSetStateValue(property._id));
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ORDER_FLOOD_CERT));
  }, [dispatch, property?._id]);

  const handleAddUnit = useCallback(() => {
    setPrimaryTab(TABS.UNITS);
    setUnitAddRequested(true);
  }, []);

  if (isNil(property)) return null;

  const address = property?.address;
  const title = addressLabel(address) || address?.street || "Property";
  const description = [address?.city, address?.state, address?.zip]
    .filter(Boolean)
    .join(", ");

  return (
    <ResizeableSidebar
      open
      hideBackdrop
      onClose={handleClose}
      title={title}
      description={description || undefined}
      descriptionInline
      headerSx={{ pb: 1 }}
      hideDivider
      bodySx={{ padding: "0 !important", background: BODY_BACKGROUND_COLOR }}
      defaultWidth={SIDEBAR_DEFAULT_WIDTH}
      titleIcon={
        <Flx
          ac
          jc
          sx={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "#e0f2fe",
            color: "#0891b2",
            "& svg": { fontSize: 20 },
          }}
        >
          <HomeWorkOutlined />
        </Flx>
      }
      headerBottomContent={
        <SidebarTabSwitcher
          variant="underline"
          hideDivider
          sx={{ borderBottom: "1px solid #eef1f4" }}
          tabs={TAB_LIST}
          value={primaryTab}
          onChange={setPrimaryTab}
        />
      }
      closeButtonStartContent={
        <PanelIconButton
          title="Delete Property"
          icon={DeleteOutlineOutlined}
          onClick={() => setConfirmDelete(true)}
        />
      }
    >
      <>
        <SidebarBodyContent py={0} px={0}>
          <TabContent tabKey={TABS.SUMMARY} activeTab={primaryTab}>
            <PropertySummaryTab
              property={property}
              onEditAddress={handleGoToAddressTab}
              onOrderFloodCert={handleOpenOrderFloodCert}
              onAddUnit={handleAddUnit}
            />
          </TabContent>

          <TabContent tabKey={TABS.ADDRESS} activeTab={primaryTab}>
            <AddressTabComponent property={property} loanId={loanId} />
          </TabContent>

          <TabContent tabKey={TABS.DETAILS} activeTab={primaryTab}>
            <PropertyDetailsTab property={property} loanId={loanId} />
          </TabContent>

          <TabContent tabKey={TABS.FILES} activeTab={primaryTab}>
            <FilesTabComponent
              files={propertyFiles}
              property={property}
              loanId={loanId}
            />
          </TabContent>

          <TabContent tabKey={TABS.COMMENTS} activeTab={primaryTab}>
            <ContentArea>
              <PanelCard
                disableCollapse
                title="Comments"
                icon={<ChatBubbleOutlineOutlined />}
              >
                <Box sx={{ mb: 2, mt: 1 }}>
                  <LoanEnhancedCommentTextBox
                    key={commentKey}
                    loan={loanDrilldown}
                    subjectPropertyIds={property?._id ? [property._id] : []}
                    onSuccessfn={() => setCommentKey((k) => k + 1)}
                  />
                </Box>
                <EnhancedCommentItemRenderer
                  loan={loanDrilldown}
                  padding={0}
                  comments={associatedComments}
                />
              </PanelCard>
            </ContentArea>
          </TabContent>

          <TabContent tabKey={TABS.MARKET_DATA} activeTab={primaryTab}>
            <ContentArea
              px={3}
              sx={
                {
                  //
                  // background: "#f8fafc",
                }
              }
            >
              <MarketDataTabContent loan={loanDrilldown} property={property} />
            </ContentArea>
          </TabContent>

          <TabContent tabKey={TABS.JSON} activeTab={primaryTab}>
            <ContentArea>
              <JsonPreview values={property} defaultCollapse={3} />
            </ContentArea>
          </TabContent>

          <TabContent tabKey={TABS.UNITS} activeTab={primaryTab}>
            <UnitsTabComponent
              property={property}
              units={units}
              loanId={loanId}
              createRequested={unitAddRequested}
              onCreateHandled={clearUnitAddRequest}
            />
          </TabContent>
        </SidebarBodyContent>
      </>

      <ConfirmationDialog
        open={confirmDelete}
        title="Delete Property?"
        message={`Are you sure you want to remove ${
          addressLabel(property?.address) || "this property"
        } from the loan? This will also remove its association with any files.`}
        // message={`Are you sure you want to remove ${
        //   property?.address?.fullAddress || "this property"
        // } from the loan? This will also remove its association with any files. You can restore it from the loan's deletedSubjectProperties list.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </ResizeableSidebar>
  );
};

const MarketDataTabContent = ({ loan, property }) => {
  const { getZillowZipcodeData } = useUnderwritingHook();
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(() => {
    if (!loan?._id) return;
    setStatus("loading");
    setError(null);
    getZillowZipcodeData({
      dealId: loan._id,
      onSuccessFn: (res) => {
        const arr = Array.isArray(res) ? res : res ? [res] : [];
        setData(arr);
        setStatus("success");
      },
      onFailFn: (err) => {
        setError(err?.message || "Failed to load market data.");
        setStatus("error");
      },
    });
  }, [getZillowZipcodeData, loan?._id]);

  useEffect(() => {
    if (isNil(data)) {
      fetchData();
    }
  }, []);

  const subjectZip = property?.address?.zip
    ? String(property.address.zip)
    : null;

  const sorted = useMemo(() => {
    if (!Array.isArray(data)) return [];
    // Surface the row that matches the subject property's zip first.
    if (!subjectZip) return data;
    const matchIdx = data.findIndex(
      (r) =>
        String(r?.ZipCode) === subjectZip ||
        String(r?.searched_zip) === subjectZip,
    );
    if (matchIdx <= 0) return data;
    return [
      data[matchIdx],
      ...data.slice(0, matchIdx),
      ...data.slice(matchIdx + 1),
    ];
  }, [data, subjectZip]);

  if (status === "loading") {
    return (
      <Flx center sx={{ py: 6 }}>
        <CircularProgress size={24} />
      </Flx>
    );
  }

  if (status === "error") {
    return (
      <Flx center column sx={{ py: 6, gap: 1 }}>
        <Txt sx={{ color: red[700], fontSize: 13 }}>
          {error || "Failed to load market data."}
        </Txt>
        <Button size="small" onClick={fetchData}>
          Retry
        </Button>
      </Flx>
    );
  }

  if (!sorted.length) {
    return (
      <Flx center column sx={{ py: 6, gap: 1 }}>
        <Txt sx={{ color: grey[500], fontSize: 13 }}>
          No Zillow data available for this loan's zip codes yet.
        </Txt>
        <Button size="small" startIcon={<SyncOutlined />} onClick={fetchData}>
          Refresh
        </Button>
      </Flx>
    );
  }

  return (
    // <SidebarSection
    //   title="Zillow Home Value Index"
    //   headerEndContent={
    //     <Button size="small" onClick={fetchData}>
    //       Refresh
    //     </Button>
    //   }
    //   // subTitle={`${sorted.length} zip${sorted.length === 1 ? "" : "s"}`}
    // >
    <Flx column g={1.5}>
      <Flx jb ac>
        <Flx column g={0.5}>
          <Flx ac g={0.75}>
            <SidebarSectionTitle
              icon={<HomeOutlined />}
              subSection
              // sx={{
              //   fontSize: 10,
              //   fontWeight: 600,
              //   letterSpacing: 0.6,
              //   textTransform: "uppercase",
              //   color: grey[500],
              // }}
            >
              {/* <Home sx={{ fontSize: 12, color: grey[500] }} /> */}
              Zillow Market Data
            </SidebarSectionTitle>
          </Flx>

          <Txt sx={{ fontSize: 11, color: grey[500] }}>
            {sorted.length} zip{sorted.length === 1 ? "" : "s"} Found
          </Txt>
        </Flx>
        <Button size="small" onClick={fetchData}>
          Refresh
        </Button>
      </Flx>
      {sorted.map((zip, i) => (
        <>
          {/* {<Divider sx={{ my: 2, borderColor: grey[200] }} />} */}
          {i === 0 ? null : <Divider sx={{ my: 2, borderColor: grey[200] }} />}
          <MarketDataCard
            key={
              zip?._id || `${zip?.ZipCode || zip?.searched_zip || "zip"}-${i}`
            }
            zip={zip}
            isSubject={
              subjectZip &&
              (String(zip?.ZipCode) === subjectZip ||
                String(zip?.searched_zip) === subjectZip)
            }
          />
        </>
      ))}
    </Flx>
    // </SidebarSection>
  );
};

// Exported: the Properties-tab card's "Market data" section renders the same
// per-zip card, so the two surfaces can't drift.
export const MarketDataCard = ({ zip, isSubject }) => {
  // Pull out the YYYY-MM-DD fields and sort chronologically
  const datedEntries = useMemo(() => {
    if (!zip) return [];
    return Object.entries(zip)
      .filter(([k]) => /^\d{4}-\d{2}-\d{2}$/.test(k))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [zip]);

  if (!zip) return null;

  const latestValue =
    zip.zillow_median ?? datedEntries[datedEntries.length - 1]?.[1];
  const latestDate =
    zip.lastReportedDate ?? datedEntries[datedEntries.length - 1]?.[0];
  const priorEntry = datedEntries[0];
  const priorValue = priorEntry?.[1];
  const priorDate = priorEntry?.[0];

  const hpa = typeof zip.HPA === "number" ? zip.HPA : null;
  const delta =
    typeof latestValue === "number" && typeof priorValue === "number"
      ? latestValue - priorValue
      : null;
  const isPositive = (hpa ?? delta ?? 0) >= 0;

  const formatCurrency = (n) =>
    typeof n === "number"
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }).format(n)
      : "—";

  const formatDate = (iso) => (iso ? formatDateShared(iso, "—", "MMM yyyy") : "—");

  const formatPct = (n) =>
    typeof n === "number" ? `${(n * 100).toFixed(2)}%` : "—";

  // Sparkline geometry (two-point: prior -> latest)
  const hasSpark =
    typeof priorValue === "number" && typeof latestValue === "number";
  const minY = hasSpark ? Math.min(priorValue, latestValue) : 0;
  const maxY = hasSpark ? Math.max(priorValue, latestValue) : 1;
  const range = maxY - minY || 1;
  const pathY = (v) => 40 - ((v - minY) / range) * 32 - 4;
  const trendColor = isPositive ? green[600] : red[600];
  const trendBg = isPositive ? green[50] : red[50];
  const trendText = isPositive ? green[700] : red[700];

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "#fff",
        // background: "#f8fafc",
        border: `1px solid ${grey[200]}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
        p: 2,
      }}
    >
      {/* Header strip */}
      <Box>
        <Flx fw jb ac>
          <Txt
            sx={{
              // mt: 0.5,
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: -0.2,
              color: grey[900],
            }}
          >
            {zip.City ? `${zip.City}, ${zip.State} ` : ""}
            {zip.ZipCode || zip.searched_zip}
          </Txt>
          <Flx ac g={0.75}>
            {isSubject && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 10,
                  bgcolor: blue[50],
                  color: blue[700],
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Subject
              </Box>
            )}
            <Flx
              ac
              g={0.5}
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 10,
                bgcolor: zip.zip_data === "success" ? green[50] : grey[100],
                color: zip.zip_data === "success" ? green[700] : grey[600],
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: zip.zip_data === "success" ? green[500] : grey[400],
                }}
              />
              <Txt sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3 }}>
                {zip.zip_data === "success" ? "Live" : "Stale"}
              </Txt>
            </Flx>
          </Flx>
        </Flx>
        {(zip.CountyName || zip.Metro) && (
          <Flx ac g={0.5} sx={{ mt: 0.25 }}>
            <LocationOn sx={{ fontSize: 11, color: grey[500] }} />
            <Txt sx={{ fontSize: 11, color: grey[500] }}>
              {[zip.CountyName, zip.Metro].filter(Boolean).join(" · ")}
            </Txt>
          </Flx>
        )}
      </Box>
      {/* Primary value */}
      <Box
        sx={{
          borderRadius: 2,
          background: "#f8fafc",
          border: `1px solid ${grey[200]}`,
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          p: 2,
          mt: 2,
          bgcolor: "#fff",
          //
          // pb: 2,
        }}
      >
        <Flx jb sx={{ alignItems: "flex-start" }}>
          <Flx column>
            <Txt
              sx={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: grey[500],
              }}
            >
              Median Home Value
            </Txt>
            <Txt
              sx={{
                mt: 0.25,
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: -0.5,
                color: grey[900],
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatCurrency(latestValue)}
            </Txt>
            <Flx ac g={0.5} sx={{ mt: 0.25 }}>
              <CalendarToday sx={{ fontSize: 11, color: grey[500] }} />
              <Txt sx={{ fontSize: 11, color: grey[500] }}>
                As of {formatDate(latestDate)}
              </Txt>
            </Flx>
          </Flx>

          {hpa !== null && (
            <Flx
              column
              aie
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: trendBg,
                color: trendText,
              }}
            >
              <Flx ac g={0.5}>
                {isPositive ? (
                  <TrendingUp sx={{ fontSize: 14 }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 14 }} />
                )}
                <Txt
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {isPositive ? "+" : ""}
                  {formatPct(hpa)}
                </Txt>
              </Flx>
              <Txt
                sx={{
                  mt: 0.25,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  opacity: 0.8,
                }}
              >
                1-Yr HPA
              </Txt>
            </Flx>
          )}
        </Flx>

        {/* Sparkline */}
        {hasSpark && (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              // bgcolor: "#fff",
              borderTop: `1px solid ${grey[100]}`,
            }}
          >
            <Box sx={{ width: "100%", height: 40 }}>
              <svg
                viewBox="0 0 100 40"
                width="100%"
                height="100%"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id={`sparkFill-${zip._id || zip.ZipCode}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={trendColor}
                      stopOpacity="0.2"
                    />
                    <stop
                      offset="100%"
                      stopColor={trendColor}
                      stopOpacity="0"
                    />
                  </linearGradient>
                </defs>
                <path
                  d={`M 0 ${pathY(priorValue)} L 100 ${pathY(latestValue)} L 100 40 L 0 40 Z`}
                  fill={`url(#sparkFill-${zip._id || zip.ZipCode})`}
                />
                <path
                  d={`M 0 ${pathY(priorValue)} L 100 ${pathY(latestValue)}`}
                  stroke={trendColor}
                  strokeWidth="1.5"
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                />
                <circle cx="0" cy={pathY(priorValue)} r="2" fill={trendColor} />
                <circle
                  cx="100"
                  cy={pathY(latestValue)}
                  r="2.5"
                  fill={trendColor}
                />
              </svg>
            </Box>
            <Flx jb sx={{ mt: 0.5 }}>
              <Txt sx={{ fontSize: 9.5, color: grey[400] }}>
                {formatDate(priorDate)}
              </Txt>
              <Txt sx={{ fontSize: 9.5, color: grey[400] }}>
                {formatDate(latestDate)}
              </Txt>
            </Flx>
          </Box>
        )}
      </Box>

      {/* Period comparison */}
      {hasSpark && (
        <Box sx={{ pb: 2, mt: 1.5 }}>
          <Flx g={1}>
            <Flx
              column
              sx={{
                // p: 1.25,
                // borderRadius: 1.5,
                // border: `1px solid ${grey[200]}`,
                flex: 1,
                borderRadius: 2,
                background: "#f8fafc",
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                p: 1.25,

                bgcolor: "#fff",
              }}
            >
              <Txt
                sx={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: grey[500],
                }}
              >
                {formatDate(priorDate)}
              </Txt>
              <Txt
                sx={{
                  mt: 0.25,
                  fontSize: 13,
                  fontWeight: 600,
                  color: grey[900],
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatCurrency(priorValue)}
              </Txt>
            </Flx>
            <Box
              sx={{
                flex: 1,
                borderRadius: 2,
                background: "#f8fafc",
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                p: 1.25,

                bgcolor: "#fff",
              }}
            >
              <Flx jb ac>
                <Txt
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: grey[500],
                  }}
                >
                  {formatDate(latestDate)}
                </Txt>
                {delta !== null && (
                  <Txt
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      color: isPositive ? green[600] : red[600],
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {isPositive ? "+" : ""}
                    {formatCurrency(delta)}
                  </Txt>
                )}
              </Flx>
              <Txt
                sx={{
                  mt: 0.25,
                  fontSize: 13,
                  fontWeight: 600,
                  color: grey[900],
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatCurrency(latestValue)}
              </Txt>
            </Box>
          </Flx>
        </Box>
      )}

      {/* Region details */}
      <Box
        sx={{
          // mx: 2,
          // mb: 2,
          flex: 1,
          borderRadius: 2,
          background: "#f8fafc",
          border: `1px solid ${grey[200]}`,
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          // p: 1.25,

          bgcolor: "#fff",
        }}
      >
        <Flx
          ac
          g={0.75}
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${grey[100]}`,
          }}
        >
          <InfoOutlined sx={{ fontSize: 12, color: grey[400] }} />
          <Txt
            sx={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: grey[600],
            }}
          >
            Region Details
          </Txt>
        </Flx>
        <Box>
          <MarketDataCardDetailRow
            label="Region Type"
            value={zip.RegionType ? String(zip.RegionType).toUpperCase() : "—"}
          />
          <MarketDataCardDetailRow
            label="Region ID"
            value={zip.RegionID ?? "—"}
            divider
          />
          <MarketDataCardDetailRow
            label="Size Rank"
            value={
              typeof zip.SizeRank === "number"
                ? `#${zip.SizeRank.toLocaleString()}`
                : "—"
            }
            divider
          />
          <MarketDataCardDetailRow
            label="ZIP Code"
            value={zip.ZipCode || zip.searched_zip || "—"}
            divider
          />
          {zip.metro_status && (
            <MarketDataCardDetailRow
              label="Metro Status"
              divider
              value={
                <Box
                  sx={{
                    display: "inline-block",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor:
                      zip.metro_status === "found" ? green[50] : grey[100],
                    color:
                      zip.metro_status === "found" ? green[700] : grey[600],
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  {zip.metro_status}
                </Box>
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const MarketDataCardDetailRow = ({ label, value, divider }) => (
  <Flx
    ac
    jb
    sx={{
      px: 1.5,
      py: 1,
      borderTop: divider ? `1px solid ${grey[100]}` : "none",
    }}
  >
    <Txt sx={{ fontSize: 11, color: grey[500] }}>{label}</Txt>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: grey[900],
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Txt>
  </Flx>
);

/* ═══════════════════════════════════════════
   SUMMARY TAB
   ═══════════════════════════════════════════ */
const PropertySummaryTab = ({
  property,
  onEditAddress,
  onOrderFloodCert,
  onAddUnit,
}) => {
  // ARV and renovation figures are construction-only — hidden for DSCR.
  const construction = isConstructionProduct(
    useSelector((state) => state.loanDrilldown?.loanProductType),
  );
  const floodZone = Boolean(property?.floodZone);
  const changeOfUse = Boolean(property?.changeOfUseFlag);
  const listedForSale = Boolean(property?.propertyListedForSale);
  const hasWarnings = floodZone || changeOfUse || listedForSale;

  const proposedType = property?.proposedPropertyType;
  const currentType = property?.propertyType;
  const typeChanging = proposedType && proposedType !== currentType;

  // Valuation derivations
  const purchasePrice = Number(property?.purchasePrice) || 0;
  const assignmentFees = Number(property?.assignmentFees) || 0;
  const additionalBasis = Number(property?.additionalBasis) || 0;
  const costBasis = purchasePrice + assignmentFees + additionalBasis;

  const asIsValue = Number(property?.asIsValue);
  const arvValue = Number(property?.arv);
  const arvGain =
    Number.isFinite(arvValue) && costBasis > 0
      ? arvValue / costBasis - 1
      : null;
  const asIsEquity =
    Number.isFinite(asIsValue) && costBasis > 0 ? asIsValue - costBasis : null;

  const resSqFt = Number(property?.residentialSqFootage) || 0;
  const comSqFt = Number(property?.commercialSqFootage) || 0;
  const afterSqFt = Number(property?.afterRenoSqFootage) || 0;
  const totalSqFt = resSqFt + comSqFt;
  const sqFtChange =
    totalSqFt > 0 && afterSqFt > 0 ? afterSqFt / totalSqFt - 1 : null;

  // Units / rent roll
  const units = Array.isArray(property?.units) ? property.units : [];
  const unitCount = units.length;
  const vacantUnits = units.filter((u) => u?.unitVacant === true).length;
  const occupancyRate =
    unitCount > 0 ? (unitCount - vacantUnits) / unitCount : null;
  const sumRent = (key) =>
    units.reduce((acc, u) => {
      const n = Number(u?.[key]);
      return Number.isFinite(n) ? acc + n : acc;
    }, 0);
  const totalMarketRent = sumRent("unitMonthlyMarketRent");
  const totalInPlaceRent = sumRent("unitMonthlyInPlaceRent");

  const valuationFields = useMemo(
    () => [
      {
        label: "Cost Basis",
        value: costBasis > 0 ? fmtMoney(costBasis) : null,
        fieldType: FIELD_TYPE.COMPUTED,
        computedTooltip: "Purchase Price + Assignment Fees + Additional Basis",
      },
      {
        label: "As-Is Equity",
        value:
          asIsEquity !== null && Number.isFinite(asIsEquity)
            ? fmtMoney(asIsEquity)
            : null,
        fieldType: FIELD_TYPE.COMPUTED,
        computedTooltip: "As-Is Value − Cost Basis",
      },
      // ARV gain + Sq Ft change are construction-only — omitted for DSCR.
      ...(construction
        ? [
            {
              label: "ARV Gain vs Basis",
              value:
                arvGain !== null
                  ? fmtPercent(arvGain, { signed: true })
                  : null,
              fieldType: FIELD_TYPE.COMPUTED,
              computedTooltip: "ARV / Cost Basis − 1",
            },
            {
              label: "Sq Ft Change",
              value:
                sqFtChange !== null && Math.abs(sqFtChange) > 0
                  ? fmtPercent(sqFtChange, { signed: true })
                  : null,
              fieldType: FIELD_TYPE.COMPUTED,
              computedTooltip:
                "After Reno Sq Ft / (Residential Sq Ft + Commercial Sq Ft) − 1",
            },
          ]
        : []),
    ],
    [costBasis, asIsEquity, arvGain, sqFtChange, construction],
  );

  const unitsFields = useMemo(
    () => [
      {
        label: "Units",
        value:
          unitCount > 0
            ? `${unitCount} total${vacantUnits > 0 ? ` • ${vacantUnits} vacant` : ""}`
            : null,
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Occupancy",
        value: occupancyRate !== null ? fmtPercent(occupancyRate) : null,
        fieldType: FIELD_TYPE.COMPUTED,
        computedTooltip: "(Total Units − Vacant Units) / Total Units",
      },
      {
        label: "Market Rent",
        value: totalMarketRent > 0 ? `${fmtMoney(totalMarketRent)} / mo` : null,
        fieldType: FIELD_TYPE.COMPUTED,
        computedTooltip: "Sum of each unit's Monthly Market Rent",
      },
      {
        label: "In-Place Rent",
        value:
          totalInPlaceRent > 0 ? `${fmtMoney(totalInPlaceRent)} / mo` : null,
        fieldType: FIELD_TYPE.COMPUTED,
        computedTooltip: "Sum of each unit's Monthly In-Place Rent",
      },
    ],
    [unitCount, vacantUnits, occupancyRate, totalMarketRent, totalInPlaceRent],
  );

  const dateFields = useMemo(
    () => [
      {
        label: "Purchase Date",
        value: fmtDate(property?.purchaseDate),
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Appraisal Date",
        value: fmtDate(property?.appraisalDate),
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Flood Cert Date",
        value: fmtDate(property?.floodCertDate),
        fieldType: FIELD_TYPE.READ_ONLY,
      },
    ],
    [property?.purchaseDate, property?.appraisalDate, property?.floodCertDate],
  );

  return (
    <ContentArea px={1.5}>
      <Flx column g={1.5} sx={{ pt: 1.5 }}>
        {/* Primary metrics — four standalone cards, no wrapping panel. */}
        <MetricCardsRow property={property} />

        {/* Type chips on the left, quick actions on the right. */}
        <Flx jb ac wrap g={1} sx={{ mt: 3 }}>
          <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
            {(currentType || typeChanging) && (
              <TypeChip>{currentType || "Not set"}</TypeChip>
            )}
            {typeChanging && (
              <>
                <Txt sx={{ fontSize: 12, color: grey[400] }}>→</Txt>
                <TypeChip highlight>{proposedType}</TypeChip>
              </>
            )}
            {property?.propertyConditionAtOrigination && (
              <TypeChip>
                Condition: {property.propertyConditionAtOrigination}
              </TypeChip>
            )}
            {property?.propertyRuralIndicator && <TypeChip>Rural</TypeChip>}
          </Flx>
          <Flx ac wrap g={1}>
            <QuickActionButton
              tone="neutral"
              onClick={onEditAddress}
              startIcon={<EditLocationAltOutlined className="thin" />}
            >
              Edit address
            </QuickActionButton>
            {/* <QuickActionButton
              tone="neutral"
              onClick={onOrderFloodCert}
              startIcon={<WaterOutlined className="thin" />}
            >
              Order flood cert
            </QuickActionButton> */}
            <QuickActionButton
              tone="neutral"
              onClick={onAddUnit}
              startIcon={<AddCircleOutline className="thin" />}
            >
              Add unit
            </QuickActionButton>
          </Flx>
        </Flx>

        <FieldsSection
          title="Valuation"
          icon={<TrendingUp />}
          badge="Calculated"
          fields={valuationFields}
        />

        {unitCount > 0 && (
          <FieldsSection
            title="Units & rent roll"
            icon={<HomeWorkOutlined />}
            fields={unitsFields}
          />
        )}

        <FieldsSection
          title="Key dates"
          icon={<CalendarToday />}
          fields={dateFields}
        />

        <FloodCertSummarySection
          property={property}
          onOrderFloodCert={onOrderFloodCert}
        />

        {hasWarnings && (
          <PanelCard title="Flags" icon={<ReportProblemOutlined />}>
            <Flx column g={1} sx={{ py: 0.5 }}>
              {floodZone && (
                <FlagRow
                  icon={
                    <WaterOutlined sx={{ fontSize: 16, color: blue[700] }} />
                  }
                  color={blue[800]}
                  label="Flood zone"
                />
              )}
              {changeOfUse && (
                <FlagRow
                  icon={
                    <ReportProblemOutlined
                      sx={{ fontSize: 16, color: orange[700] }}
                    />
                  }
                  color={orange[800]}
                  label="Change of use"
                />
              )}
              {listedForSale && (
                <FlagRow
                  icon={
                    <ReportProblemOutlined
                      sx={{ fontSize: 16, color: red[600] }}
                    />
                  }
                  color={red[700]}
                  label="Listed for sale"
                />
              )}
            </Flx>
          </PanelCard>
        )}

        {property?.subjectPropertyNotes && (
          <PanelCard title="Notes" icon={<DescriptionOutlined />}>
            <Box sx={{ py: 0.5 }}>
              <Txt
                sx={{
                  fontSize: 13,
                  color: grey[700],
                  whiteSpace: "pre-wrap",
                }}
              >
                {property.subjectPropertyNotes}
              </Txt>
            </Box>
          </PanelCard>
        )}
      </Flx>
    </ContentArea>
  );
};

const TypeChip = ({ children, highlight }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: "12px",
      py: "5px",
      borderRadius: "999px",
      bgcolor: highlight ? blue[50] : "#ffffff",
      color: highlight ? blue[800] : grey[800],
      border: highlight
        ? `1px solid ${blue[200]}`
        : `1px solid rgba(0, 0, 0, 0.15)`,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4,
      "& svg": { fontSize: "14px !important" },
    }}
  >
    {children}
  </Box>
);

const FlagRow = ({ icon, label, color }) => (
  <Flx ac g={0.75}>
    {icon}
    <Txt sx={{ fontSize: 12.5, fontWeight: 600, color }}>{label}</Txt>
  </Flx>
);

/* ─────────────────────────────────────────────
   Flood Certificate summary section
   ─────────────────────────────────────────────
   Renders the per-property `lastFloodCertResult` block in the Summary tab —
   determination details, last-pull metadata, vendor messages, and a CTA
   wired to the relevant follow-up action (retry order, status_query, etc.).
*/

const FOLLOW_UP_LABELS = {
  retry_with_apn: "Retry with APN",
  fix_address: "Edit address & retry",
  fix_borrower: "Add borrower & retry",
  vendor_outage: "Try again",
  pending_vendor_review: "Check status",
  admin_retrieve: "Check status",
  auth_failed: null, // no CTA; admin must fix Xactus account
};

const FloodCertSummarySection = ({ property, onOrderFloodCert }) => {
  const dispatch = useDispatch();
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  const { queryFloodOrderStatus } = useUnderwritingHook();
  const [statusChecking, setStatusChecking] = useState(false);

  const result = property?.lastFloodCertResult;

  const handleStatusCheck = useCallback(() => {
    const certId = result?.floodCertificationId;
    if (!loanId || !certId) return;
    setStatusChecking(true);
    queryFloodOrderStatus({
      loanId,
      floodCertificationId: certId,
      onSuccessFn: (response) => {
        if (response?.subjectProperties) {
          dispatch(
            loanDrilldownSetKey({
              key: "subjectProperties",
              value: response.subjectProperties,
            }),
          );
        }
        if (response?.loanDocuments) {
          dispatch(
            loanDrilldownSetKey({
              key: "loanDocuments",
              value: response.loanDocuments,
            }),
          );
        }
      },
      onCompleteFn: () => setStatusChecking(false),
    });
  }, [loanId, result?.floodCertificationId, queryFloodOrderStatus, dispatch]);

  // Empty state — never ordered.
  // TEMP: hide the section entirely when there's no flood cert result, so
  // the card shows nothing rather than an "Order Flood Cert" prompt.
  // Restore the empty-state block below to bring the order CTA back.
  if (!result) {
    return null;
    // return (
    //   <SidebarSection title="Flood Certificate">
    //     <Flx column g={1.5} sx={{ px: 1, py: 0.5 }}>
    //       <Txt sx={{ fontSize: 12, color: grey[600] }}>
    //         No flood certificate has been ordered for this property yet.
    //       </Txt>
    //       <Box>
    //         <Button
    //           size="small"
    //           variant="outlined"
    //           startIcon={<WaterOutlined sx={{ fontSize: 16 }} />}
    //           onClick={onOrderFloodCert}
    //           sx={{ textTransform: "none" }}
    //         >
    //           Order Flood Cert
    //         </Button>
    //       </Box>
    //     </Flx>
    //   </SidebarSection>
    // );
  }

  const status = result.status;
  const followUp = result.followUp || {};
  const ctaLabel = followUp?.category
    ? FOLLOW_UP_LABELS[followUp.category]
    : null;
  const ctaIsStatusCheck =
    followUp?.category === "pending_vendor_review" ||
    followUp?.category === "admin_retrieve";

  let severity = "info";
  let title = "Flood Certificate";
  let icon = <CheckCircleOutline sx={{ fontSize: 20 }} />;
  if (status === "success") {
    severity = "success";
    title = "Flood determination on file";
    icon = <CheckCircleOutline sx={{ fontSize: 20 }} />;
  } else if (status === "pending") {
    severity = "info";
    title = "Order pending vendor response";
    icon = <HourglassEmpty sx={{ fontSize: 20 }} />;
  } else if (status === "skipped") {
    severity = "warning";
    title = "Order skipped";
    icon = <ReportProblemOutlined sx={{ fontSize: 20 }} />;
  } else if (status === "error") {
    severity = "error";
    title = "Order needs attention";
    icon = <ErrorOutline sx={{ fontSize: 20 }} />;
  }

  const determination = {
    "Flood zone": result.floodZone,
    "Special Flood Hazard Area": isNil(result.inSpecialFloodHazardArea)
      ? null
      : result.inSpecialFloodHazardArea
        ? "Yes"
        : "No",
    "NFIP community": [result.nfipCommunityId, result.nfipCommunityName]
      .filter(Boolean)
      .join(" — "),
    "NFIP panel": [result.nfipPanelId, formatDateShared(result.nfipPanelDate, null)]
      .filter(Boolean)
      .join(" / "),
    "Map date": formatDateShared(result.mapDate, null),
    "Determination date": formatDateShared(result.determinationDate, null),
  };
  const determinationEntries = Object.entries(determination).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );

  // Surface the card itself as an "alert" variant when the order needs
  // attention or was skipped — per the design doc, alert cards are not
  // collapsible so the operator can't visually dismiss something actionable.
  const cardVariant =
    severity === "error" || severity === "warning" ? "alert" : "standard";

  return (
    <PanelCard
      title="Flood certificate"
      icon={<WaterOutlined />}
      variant={cardVariant}
      disableCollapse={cardVariant === "alert"}
    >
      <Flx column g={1.5} sx={{ py: 0.5 }}>
        <Alert
          severity={severity}
          icon={icon}
          sx={{ "& .MuiAlert-message": { width: "100%" } }}
        >
          <AlertTitle sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.25 }}>
            {title}
          </AlertTitle>
          {followUp?.userMessage ? (
            <Txt sx={{ fontSize: 12, lineHeight: 1.5 }}>
              {followUp.userMessage}
            </Txt>
          ) : result.reason ? (
            <Txt sx={{ fontSize: 12, lineHeight: 1.5 }}>{result.reason}</Txt>
          ) : null}
        </Alert>

        {determinationEntries.length > 0 && (
          <Box
            sx={{
              border: `1px solid ${blue[100]}`,
              borderRadius: 2,
              p: 1.25,
              background: blue[50],
            }}
          >
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: blue[800],
                mb: 0.75,
              }}
            >
              Determination
            </Txt>
            <Flx column g={0.5}>
              {determinationEntries.map(([k, v]) => (
                <Flx key={k} ac jb g={2}>
                  <Txt sx={{ fontSize: 12, color: grey[700] }}>{k}</Txt>
                  <Txt sx={{ fontSize: 12, fontWeight: 600, color: grey[900] }}>
                    {v}
                  </Txt>
                </Flx>
              ))}
            </Flx>
          </Box>
        )}

        <Flx column g={0.4} sx={{ px: 0.25 }}>
          {result.floodCertificationId && (
            <Flx ac jb g={1}>
              <Txt sx={{ fontSize: 11, color: grey[500] }}>
                Certification ID
              </Txt>
              <Txt
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: grey[800],
                  fontFamily: "ui-monospace, Menlo, monospace",
                }}
              >
                {result.floodCertificationId}
              </Txt>
            </Flx>
          )}
          {result.pulledAt && (
            <Flx ac jb g={1}>
              <Txt sx={{ fontSize: 11, color: grey[500] }}>Last attempt</Txt>
              <Txt sx={{ fontSize: 11.5, color: grey[800] }}>
                {formatDateShared(result.pulledAt, result.pulledAt)}
                {result.pulledBy ? ` · ${result.pulledBy}` : ""}
              </Txt>
            </Flx>
          )}
        </Flx>

        {!isNil(result.vendorMessages) && result.vendorMessages.length > 0 && (
          <Box
            sx={{
              border: `1px solid ${grey[200]}`,
              borderRadius: 2,
              p: 1.25,
              background: grey[50],
            }}
          >
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: grey[600],
                mb: 0.5,
              }}
            >
              Vendor messages
            </Txt>
            <Flx column g={0.4}>
              {result.vendorMessages.map((m, i) => (
                <Txt
                  key={i}
                  component="div"
                  sx={{ fontSize: 11.5, color: grey[800], lineHeight: 1.5 }}
                >
                  • {m}
                </Txt>
              ))}
            </Flx>
          </Box>
        )}

        {followUp?.adminMessage && followUp?.category === "auth_failed" && (
          <Alert
            severity="info"
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
          >
            <AlertTitle sx={{ fontSize: 12, fontWeight: 700, mb: 0.25 }}>
              Admin notes
            </AlertTitle>
            <Txt sx={{ fontSize: 11.5, lineHeight: 1.5 }}>
              {followUp.adminMessage}
            </Txt>
          </Alert>
        )}

        <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
          {ctaLabel && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={
                ctaIsStatusCheck ? (
                  <RefreshOutlined sx={{ fontSize: 16 }} />
                ) : (
                  <WaterOutlined sx={{ fontSize: 16 }} />
                )
              }
              onClick={ctaIsStatusCheck ? handleStatusCheck : onOrderFloodCert}
              loading={ctaIsStatusCheck && statusChecking}
              loadingPosition="end"
              sx={{ textTransform: "none" }}
            >
              {ctaLabel}
            </Button>
          )}
          {/* Always offer a plain "re-order" path, regardless of follow-up
              state — useful when the operator wants a fresh determination
              outside the follow-up flow. */}
          {!ctaIsStatusCheck && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<WaterOutlined sx={{ fontSize: 16 }} />}
              onClick={onOrderFloodCert}
              sx={{ textTransform: "none" }}
            >
              {ctaLabel ? "Place new order" : "Order again"}
            </Button>
          )}
        </Flx>
      </Flx>
    </PanelCard>
  );
};

/* Primary metrics — four standalone bordered cards in a row. Each shows an
   uppercase eyebrow label and a large value, or an explicit "Not set" when
   empty (no em-dashes). Wraps to two-up on a narrow sidebar. */
const MetricCardsRow = ({ property }) => {
  // ARV is construction-only — hidden for DSCR.
  const construction = isConstructionProduct(
    useSelector((state) => state.loanDrilldown?.loanProductType),
  );
  const metrics = [
    { label: "As-Is", value: fmtMoney(property?.asIsValue) },
    ...(construction
      ? [
          {
            label: "ARV",
            value: fmtMoney(property?.arv),
            valueColor: property?.arv ? green[700] : null,
          },
        ]
      : []),
    { label: "Purchase", value: fmtMoney(property?.purchasePrice) },
    { label: "Sq Ft", value: fmtNumber(property?.residentialSqFootage) },
  ];
  return (
    <Flx g={1} sx={{ flexWrap: "wrap" }}>
      {metrics.map((m) => (
        <KpiWidget
          sx={{ flex: "1 1 120px" }}
          key={m.label}
          label={m.label}
          value={m.value}
          valueColor={m.valueColor}
        />
      ))}
    </Flx>
  );
};

const MetricCard = ({ label, value, valueColor }) => {
  const hasValue = value != null && value !== "";
  return (
    <Box
      sx={{
        flex: "1 1 120px",
        minWidth: 110,
        background: PANEL_TOKENS.color.bgCard,
        border: `0.5px solid ${PANEL_TOKENS.color.borderDefault}`,
        borderRadius: PANEL_TOKENS.radius.md,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Txt
        sx={{
          fontSize: 10,
          fontWeight: PANEL_TOKENS.weight.medium,
          color: PANEL_TOKENS.color.textTertiary,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Txt>
      <Txt
        sx={{
          fontSize: PANEL_TOKENS.text.lg,
          fontWeight: PANEL_TOKENS.weight.medium,
          color: hasValue
            ? valueColor || PANEL_TOKENS.color.textPrimary
            : PANEL_TOKENS.color.textTertiary,
          mt: 0.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {hasValue ? value : "Not set"}
      </Txt>
    </Box>
  );
};

/* ═══════════════════════════════════════════
   DETAILS TAB — collapsible sections w/ DetailRows
   ═══════════════════════════════════════════ */
const FIELD_LABELS_FOR_BANNER = {
  propertyType: "Property Type",
  proposedPropertyType: "Proposed Property Type",
  propertyConditionAtOrigination: "Condition at Origination",
  propertyRuralIndicator: "Rural Indicator",
  condoConversion: "Condo Conversion",
  changeOfUseFlag: "Change of Use",
  residentialSqFootage: "Residential Sq Ft",
  commercialSqFootage: "Commercial Sq Ft",
  afterRenoSqFootage: "After Reno Sq Ft",
  stringAddressFinalTitle: "Final Title Address",
  purchasePrice: "Purchase Price",
  purchaseDate: "Purchase Date",
  additionalBasis: "Additional Basis",
  assignmentFees: "Assignment Fees",
  sellersConcession: "Seller's Concession",
  asIsValue: "As-Is Value",
  secondaryAsIsValue: "Secondary As-Is Value",
  arv: "ARV",
  secondaryArv: "Secondary ARV",
  appraisalDate: "Appraisal Date",
  secondaryAppraisalDate: "Secondary Appraisal Date",
  replacementCostEstimate: "Replacement Cost Estimate",
  propertyListedForSale: "Listed For Sale",
  lastListedForSaleDate: "Last Listed Date",
  lastListedForSalePrice: "Last Listed Price",
  floodZone: "Flood Zone",
  floodCertDate: "Flood Cert Date",
  annualTaxes: "Annual Taxes",
  annualInsurance: "Annual Insurance",
  annualFloodInsurance: "Annual Flood Insurance",
  annualHoaFees: "Annual HOA Fees",
  annualPropertyMgmtFees: "Annual Property Mgmt Fees",
  annualOtherExpense: "Annual Other Expenses",
  constructionBudget: "Construction Budget",
  financedBudgetPercent: "Financed Budget %",
  interestReserves: "Interest Reserves",
  budgetIsFinal: "Budget Is Final",
  budgetMatchesAppraisal: "Budget Matches Appraisal",
  finalBudgetUploaded: "Final Budget Uploaded",
  finalBudgetFeasible: "Final Budget Feasible",
};

const formatPendingForBanner = (key, raw) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (typeof raw === "boolean") return raw ? "Yes" : "No";
  if (key.toLowerCase().includes("date")) return formatDateShared(raw, raw);
  if (
    /price|value|fee|basis|reserve|budget|insurance|taxes|expense|concession|estimate/i.test(
      key,
    ) &&
    typeof raw === "number"
  ) {
    return formatDollar(raw, raw);
  }
  if (/percent|Pct/.test(key) && typeof raw === "number") {
    return formatPercent(raw > 1 ? raw / 100 : raw);
  }
  return String(raw);
};

/* Primary "Address" tab — read-only address fields + inline-editable
   Final Title Address. Wraps in its own RffFormChanges so the pending-
   changes banner shows and saves are independent of the Details tab. */
const PropertyAddressTab = ({ property, loanId }) => {
  const dispatch = useDispatch();
  const { updateSubjectProperty } = useUnderwritingHook();
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(
    (changedValues) => {
      setSaving(true);
      updateSubjectProperty({
        loanId,
        propertyId: property?._id,
        data: changedValues,
        onSuccessFn: (response) => {
          dispatch(loanDrilldownSet(response));
          dispatch(loanPipelineClear(response));
        },
        onFailFn: () => setSaving(false),
        onCompleteFn: () => setSaving(false),
      });
    },
    [loanId, property?._id, updateSubjectProperty, dispatch],
  );

  return (
    <Box
      sx={
        {
          //
          // background: "#f5f5f5",
        }
      }
    >
      <RffFormChanges
        initialValues={property}
        onSubmit={handleSave}
        loading={saving}
        suppressFooter
        // Floating save pill: rendered at the bottom and kept mounted so it can
        // slide out / hold its "Saved" state after the last change clears.
        hasChangesContentPosition="bottom"
        persistChangesContent
        hasChangesContent={
          <RffPendingChangesBanner
            initialValues={property}
            getFieldLabel={(k) => FIELD_LABELS_FOR_BANNER[k] || k}
            formatField={formatPendingForBanner}
            loading={saving}
          />
        }
      >
        <ContentArea>
          {/* The map now lives inside PropertyAddressEditor's
              "Official Address" card, above the editor. */}
          <PropertyAddressEditor property={property} loanId={loanId} />
          <FinalTitleAddressSection />
          <AddressSection property={property} />
        </ContentArea>
      </RffFormChanges>
    </Box>
  );
};

// Which Address-tab layout the sidebar renders:
//   "card"    — one card: map, official address, address on title, details
//               (PropertyAddressCardTab)
//   "classic" — Official Address section + title field + collapsible details
//               (PropertyAddressTab, above)
// Flip back to "classic" to revert; both stay in the codebase.
const ADDRESS_TAB_LAYOUT = "card";

// Which Documents-tab layout the sidebar renders:
//   "assigned" — the Properties-tab card's Assigned Documents rail
//                (PropertyAssignedDocumentsTab)
//   "classic"  — Upload card + inline-editable files table (PropertyFilesTab)
// Flip back to "classic" to revert; both stay in the codebase. (The picked
// component, FilesTabComponent, is resolved below PropertyFilesTab.)
const FILES_TAB_LAYOUT = "assigned";
const AddressTabComponent =
  ADDRESS_TAB_LAYOUT === "card" ? PropertyAddressCardTab : PropertyAddressTab;

const PropertyDetailsTab = ({ property, loanId }) => {
  const dispatch = useDispatch();
  const loanPurpose = useSelector((state) => state.loanDrilldown?.loanPurpose);
  // Construction/budget surfaces are hidden for DSCR (and other non-construction
  // products) — the whole Budget subtab comes out. Every other
  // non-DSCR product keeps the subtab with only the interest-reserve rows.
  const loanProductType = useSelector(
    (state) => state.loanDrilldown?.loanProductType,
  );
  const construction = isConstructionProduct(loanProductType);
  const interestReserve = isInterestReserveProduct(loanProductType);
  const { updateSubjectProperty } = useUnderwritingHook();
  const [quickFilter, setQuickFilter] = useState("");
  const [detailTab, setDetailTab] = useState(PROPERTY_DETAIL_TABS.ALL);
  const [saving, setSaving] = useState(false);

  // Expand/collapse all for this tab's cards. Only the token change matters to
  // PanelCard, so collapsing one by hand afterwards sticks.
  const [cardSignal, setCardSignal] = useState(null);
  const signalCards = useCallback((open) => {
    setCardSignal((s) => ({ token: (s?.token || 0) + 1, open }));
  }, []);

  // Purchase fields apply to a purchase AND a refi — on a refi the same
  // `purchasePrice` / `purchaseDate` carry the ORIGINAL purchase (the model has
  // no separate "original" fields). Any other purpose still shows them once
  // the property carries purchase data.
  const isRefi = REFI_PURPOSES.includes(loanPurpose);
  const isPurchaseApplicable = useMemo(
    () =>
      loanPurpose === "Purchase" ||
      isRefi ||
      Boolean(property?.purchasePrice) ||
      Boolean(property?.purchaseDate),
    [loanPurpose, isRefi, property?.purchasePrice, property?.purchaseDate],
  );

  const showSection = useCallback(
    (section) =>
      detailTab === PROPERTY_DETAIL_TABS.ALL || detailTab === section,
    [detailTab],
  );

  const subTabs = useMemo(
    () =>
      Object.values(PROPERTY_DETAIL_TABS)
        .filter(
          (t) => t !== PROPERTY_DETAIL_TABS.PURCHASE || isPurchaseApplicable,
        )
        // Budget subtab is construction / interest-reserve only — hide for DSCR.
        .filter((t) => t !== PROPERTY_DETAIL_TABS.BUDGET || interestReserve)
        .map((t) => ({ label: t, value: t })),
    [isPurchaseApplicable, interestReserve],
  );

  // Card layout — shared with the borrower sidebar. Every sub-tab but "All"
  // renders exactly one section; "All" renders every tab's section, which is
  // `subTabs` minus the "All" entry itself (so the purchase gating is
  // accounted for without a second count to keep in sync).
  const cardCount =
    detailTab === PROPERTY_DETAIL_TABS.ALL ? subTabs.length - 1 : 1;
  const { ref: attachMasonryRef, sx: masonrySx } = useCardMasonry(cardCount);

  const handleSave = useCallback(
    (changedValues) => {
      setSaving(true);
      updateSubjectProperty({
        loanId,
        propertyId: property?._id,
        data: changedValues,
        onSuccessFn: (response) => {
          dispatch(loanDrilldownSet(response));
          dispatch(loanPipelineClear(response));
        },
        onFailFn: () => setSaving(false),
        onCompleteFn: () => setSaving(false),
      });
    },
    [loanId, property?._id, updateSubjectProperty, dispatch],
  );

  return (
    <PanelCardCollapseProvider value={cardSignal}>
      <Box>
      <RffFormChanges
        initialValues={property}
        onSubmit={handleSave}
        loading={saving}
        suppressFooter
        // Floating save pill: rendered at the bottom and kept mounted so it can
        // slide out / hold its "Saved" state after the last change clears.
        hasChangesContentPosition="bottom"
        persistChangesContent
        hasChangesContent={
          <RffPendingChangesBanner
            initialValues={property}
            getFieldLabel={(k) => FIELD_LABELS_FOR_BANNER[k] || k}
            formatField={formatPendingForBanner}
            loading={saving}
          />
        }
      >
        <>
          {/* Sub-tab switcher over search + expand/collapse, both pinned. One
              sticky wrapper rather than two stacked sticky rows — the
              switcher's height changes when it wraps, so a hard `top` offset on
              the second row would drift. Mirrors the borrower sidebar. */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              background: "#ffffff",
              borderBottom: `1px solid ${grey[200]}`,
            }}
          >
            <SubTabSwitcher
              tabs={subTabs}
              value={detailTab}
              onChange={setDetailTab}
            />
            <Flx ac g={1} sx={{ px: 1, py: 1 }}>
              <Box sx={{ flex: "0 1 320px", minWidth: 160 }}>
                <SearchInput
                  variant="paper"
                  rounded
                  placeholder="Search property fields..."
                  autoFocus
                  fullWidth
                  onClear={() => setQuickFilter("")}
                  value={quickFilter}
                  onChange={setQuickFilter}
                  sx={{
                    input: {
                      fontSize: "11px",
                      height: "14px",
                      px: 0.5,
                      py: 1,
                    },
                    svg: { fontSize: "14px !important" },
                  }}
                />
              </Box>
              <CardCollapseButtons onSignal={signalCards} />
            </Flx>
          </Box>

          <ContentArea>
            <Box ref={attachMasonryRef} sx={masonrySx}>
              {showSection(PROPERTY_DETAIL_TABS.DETAILS) && (
                <PropertyDetailsSection
                  property={property}
                  quickFilter={quickFilter}
                />
              )}
              {showSection(PROPERTY_DETAIL_TABS.VALUATION) && (
                <ValuationSection
                  property={property}
                  quickFilter={quickFilter}
                />
              )}
              {isPurchaseApplicable &&
                showSection(PROPERTY_DETAIL_TABS.PURCHASE) && (
                  <PurchaseSection
                    property={property}
                    quickFilter={quickFilter}
                    isRefi={isRefi}
                  />
                )}
              {showSection(PROPERTY_DETAIL_TABS.LISTING) && (
                <ListingSection property={property} quickFilter={quickFilter} />
              )}
              {showSection(PROPERTY_DETAIL_TABS.FLOOD) && (
                <FloodSection property={property} quickFilter={quickFilter} />
              )}
              {showSection(PROPERTY_DETAIL_TABS.COSTS) && (
                <CostsSection property={property} quickFilter={quickFilter} />
              )}
              {interestReserve && showSection(PROPERTY_DETAIL_TABS.BUDGET) && (
                <ConstructionBudgetSection
                  property={property}
                  quickFilter={quickFilter}
                  reservesOnly={!construction}
                />
              )}
              {showSection(PROPERTY_DETAIL_TABS.NOTES) && (
                <PropertyNotesSection
                  property={property}
                  quickFilter={quickFilter}
                />
              )}
            </Box>
          </ContentArea>
        </>
      </RffFormChanges>
      </Box>
    </PanelCardCollapseProvider>
  );
};

const AddressSection = ({ property, quickFilter }) => {
  // Reads straight from the saved Redux record — PropertyAddressEditor
  // (the section above) handles its own save and dispatches
  // loanDrilldownSet, so this view re-renders with the new address as
  // soon as the save returns.
  const a = property?.address;
  const fields = useMemo(
    () => [
      {
        label: "Full Address",
        value: addressLabel(a),
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address full",
      },
      {
        label: "Street Number",
        value: a?.streetNumber,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address street number",
      },
      {
        label: "Street Name",
        value: a?.streetName,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address street name",
      },
      {
        label: "Unit Number",
        value: a?.addressUnitNumber || a?.unit,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address unit apartment apt",
      },
      {
        label: "City",
        value: a?.city,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address city",
      },
      {
        label: "State",
        value: a?.state,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address state",
      },
      {
        label: "Zip",
        value: a?.zip,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address zip postal",
      },
      {
        label: "County",
        value: a?.county,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address county",
      },
      {
        label: "Country",
        value: a?.country,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address country",
      },
      {
        label: "APN",
        value: a?.apn,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "apn assessor parcel number",
      },
      {
        label: "Display Address",
        value: a?.displayAddress,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address display manual override",
      },
      {
        label: "Geolocation Status",
        value: a?.geolocation,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "geolocation match partial failed",
      },
      {
        label: "Latitude",
        value: a?.latitude,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "latitude coordinates",
      },
      {
        label: "Longitude",
        value: a?.longitude,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "longitude coordinates",
      },
    ],
    [a],
  );
  return (
    <FieldsSection
      title="Address details"
      icon={<LocationOn />}
      fields={fields}
      defaultOpen={false}
      quickFilter={quickFilter}
    />
  );
};

/* Bottom section of the Address tab — the manually-entered string that
   appears on the final title document. Edits go through the parent
   RffFormChanges as `stringAddressFinalTitle`. */
const FinalTitleAddressSection = () => {
  const fields = useMemo(
    () => [
      {
        label: "Final Title Address",
        name: "stringAddressFinalTitle",
        editor: { type: "text" },
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms:
          "final title address how it should appear closing recorded",
      },
    ],
    [],
  );
  return (
    <FieldsSection
      title="Final address on title"
      icon={<DescriptionOutlined />}
      fields={fields}
    />
  );
};

const PropertyDetailsSection = ({ property, quickFilter }) => {
  const resSqFt = Number(property?.residentialSqFootage) || 0;
  const comSqFt = Number(property?.commercialSqFootage) || 0;
  const afterSqFt = Number(property?.afterRenoSqFootage) || 0;
  const totalSqFt = resSqFt + comSqFt;
  const sqFtChange =
    totalSqFt > 0 && afterSqFt > 0 ? afterSqFt / totalSqFt - 1 : null;

  // Renovation / change-of-use fields are construction-only — hidden for DSCR.
  const construction = isConstructionProduct(
    useSelector((state) => state.loanDrilldown?.loanProductType),
  );

  const fields = useMemo(() => {
    const statusArray = Array.isArray(property?.propertyStatusAtOrigination)
      ? property.propertyStatusAtOrigination
      : property?.propertyStatusAtOrigination
        ? [property.propertyStatusAtOrigination]
        : [];
    const rows = [
      {
        label: "Property Type",
        name: "propertyType",
        editor: { type: "select", options: selectOptionsPropertyType },
        value: property?.propertyType,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "property type",
      },
      {
        label: "Proposed Property Type",
        name: "proposedPropertyType",
        editor: { type: "select", options: selectOptionsPropertyType },
        value: property?.proposedPropertyType,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "proposed property type",
      },
      {
        label: "Condition at Origination",
        name: "propertyConditionAtOrigination",
        editor: {
          type: "select",
          options: selectOptionsPropertyConditionAtOrigination,
        },
        value: property?.propertyConditionAtOrigination,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "property condition c1 c2 c3 c4 c5 c6",
      },
      {
        label: "Property Status at Origination",
        name: "propertyStatusAtOrigination",
        editor: {
          type: "selectMultiple",
          options: selectOptionsPropertyStatusAtOrigination,
        },
        value: statusArray.length > 0 ? statusArray.join(", ") : null,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "property status at origination",
      },
      {
        label: "Residential Sq Ft",
        name: "residentialSqFootage",
        editor: { type: "number" },
        formatter: fmtNumber,
        value: fmtNumber(property?.residentialSqFootage),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "residential square footage",
      },
      {
        label: "Commercial Sq Ft",
        name: "commercialSqFootage",
        editor: { type: "number" },
        formatter: fmtNumber,
        value: fmtNumber(property?.commercialSqFootage),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "commercial square footage",
      },
      {
        label: "After Reno Sq Ft",
        name: "afterRenoSqFootage",
        editor: { type: "number" },
        formatter: fmtNumber,
        value: fmtNumber(property?.afterRenoSqFootage),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "after renovation square footage",
      },
      {
        label: "Sq Ft Change",
        value: fmtPercent(sqFtChange, { signed: true }),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "square footage change percent",
        computedTooltip:
          "After Reno Sq Ft / (Residential Sq Ft + Commercial Sq Ft) − 1",
      },
      {
        label: "Rural Indicator",
        name: "propertyRuralIndicator",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.propertyRuralIndicator),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "rural indicator",
      },
      {
        label: "Condo Conversion",
        name: "condoConversion",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.condoConversion),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "condo conversion",
      },
      {
        label: "Change of Use",
        name: "changeOfUseFlag",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.changeOfUseFlag),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "change of use",
      },
      {
        label: "Salesforce ID",
        value: property?.salesforceId,
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "salesforce id integration",
      },
      {
        label: "Last Integration Sync",
        value: fmtDate(property?.last_integration_sync),
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "last integration sync salesforce hubspot",
      },
    ];
    if (construction) return rows;
    // Drop the construction/renovation-only rows for DSCR.
    const constructionOnly = new Set([
      "proposedPropertyType",
      "afterRenoSqFootage",
      "condoConversion",
      "changeOfUseFlag",
    ]);
    return rows.filter(
      (f) => !constructionOnly.has(f.name) && f.label !== "Sq Ft Change",
    );
  }, [property, sqFtChange, construction]);
  return (
    <FieldsSection
      title="Property details"
      icon={<HomeWorkOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const PurchaseSection = ({ property, quickFilter, isRefi }) => {
  const purchasePrice = Number(property?.purchasePrice) || 0;
  const assignmentFees = Number(property?.assignmentFees) || 0;
  const additionalBasis = Number(property?.additionalBasis) || 0;
  const costBasis = purchasePrice + assignmentFees + additionalBasis;
  // Additional Basis is a construction/rehab basis input — hidden for DSCR.
  const construction = isConstructionProduct(
    useSelector((state) => state.loanDrilldown?.loanProductType),
  );

  const fields = useMemo(
    () => [
      {
        label: isRefi ? "Original Purchase Price" : "Purchase Price",
        name: "purchasePrice",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.purchasePrice),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "purchase price original",
      },
      {
        label: isRefi ? "Original Purchase Date" : "Purchase Date",
        name: "purchaseDate",
        editor: { type: "date" },
        formatter: fmtDate,
        value: fmtDate(property?.purchaseDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "purchase date original",
      },
      ...(construction
        ? [
            {
              label: "Additional Basis",
              name: "additionalBasis",
              editor: { type: "dollar" },
              formatter: fmtMoney,
              value: fmtMoney(property?.additionalBasis),
              fieldType: FIELD_TYPE.EDITABLE,
              searchTerms: "additional basis",
            },
          ]
        : []),
      {
        label: "Assignment Fees",
        name: "assignmentFees",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.assignmentFees),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "assignment fees",
      },
      {
        label: "Seller's Concession",
        name: "sellersConcession",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.sellersConcession),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "sellers concession",
      },
      {
        label: "Cost Basis",
        value: fmtMoney(costBasis),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "cost basis",
        computedTooltip: "Purchase Price + Assignment Fees + Additional Basis",
      },
    ],
    [property, costBasis, construction, isRefi],
  );
  return (
    <FieldsSection
      title={isRefi ? "Original Purchase" : "Purchase"}
      icon={<LocalOfferOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const ValuationSection = ({ property, quickFilter }) => {
  // ARV / after-repair value is construction-only — hide it for DSCR (and other
  // non-construction products). As-Is stays (DSCR's LTAIV runs against it).
  const construction = isConstructionProduct(
    useSelector((state) => state.loanDrilldown?.loanProductType),
  );
  const asIsValue = Number(property?.asIsValue);
  const secondaryAsIsValue = Number(property?.secondaryAsIsValue);
  const secondaryAsIsVariance =
    Number.isFinite(asIsValue) &&
    asIsValue > 0 &&
    Number.isFinite(secondaryAsIsValue)
      ? secondaryAsIsValue / asIsValue - 1
      : null;

  const arv = Number(property?.arv);
  const secondaryArv = Number(property?.secondaryArv);
  const secondaryArvVariance =
    Number.isFinite(arv) && arv > 0 && Number.isFinite(secondaryArv)
      ? secondaryArv / arv - 1
      : null;

  const fields = useMemo(
    () => [
      {
        label: "As-Is Value",
        name: "asIsValue",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.asIsValue),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "as is value",
      },
      {
        label: "Secondary As-Is Value",
        name: "secondaryAsIsValue",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.secondaryAsIsValue),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "secondary as is value cda",
      },
      {
        label: "Secondary As-Is Variance",
        value: fmtPercent(secondaryAsIsVariance, { signed: true }),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "secondary as is variance",
        computedTooltip: "Secondary As-Is Value / As-Is Value − 1",
      },
      // ARV rows are construction-only — omitted for DSCR / non-construction.
      ...(construction
        ? [
            {
              label: "ARV",
              name: "arv",
              editor: { type: "dollar" },
              formatter: fmtMoney,
              value: fmtMoney(property?.arv),
              fieldType: FIELD_TYPE.EDITABLE,
              searchTerms: "arv after repair value",
            },
            {
              label: "Secondary ARV",
              name: "secondaryArv",
              editor: { type: "dollar" },
              formatter: fmtMoney,
              value: fmtMoney(property?.secondaryArv),
              fieldType: FIELD_TYPE.EDITABLE,
              searchTerms: "secondary arv",
            },
            {
              label: "Secondary ARV Variance",
              value: fmtPercent(secondaryArvVariance, { signed: true }),
              fieldType: FIELD_TYPE.COMPUTED,
              searchTerms: "secondary arv variance",
              computedTooltip: "Secondary ARV / ARV − 1",
            },
          ]
        : []),
      {
        label: "Appraisal Date",
        name: "appraisalDate",
        editor: { type: "date" },
        formatter: fmtDate,
        value: fmtDate(property?.appraisalDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "appraisal date",
      },
      {
        label: "Secondary Appraisal Date",
        name: "secondaryAppraisalDate",
        editor: { type: "date" },
        formatter: fmtDate,
        value: fmtDate(property?.secondaryAppraisalDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "secondary appraisal date",
      },
      {
        label: "Replacement Cost Estimate",
        name: "replacementCostEstimate",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.replacementCostEstimate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "replacement cost estimate rce insurance",
      },
    ],
    [property, secondaryAsIsVariance, secondaryArvVariance, construction],
  );
  return (
    <FieldsSection
      title="Valuation"
      icon={<TrendingUp />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const ListingSection = ({ property, quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        label: "Listed For Sale",
        name: "propertyListedForSale",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.propertyListedForSale),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "listed for sale",
      },
      {
        label: "Last Listed Date",
        name: "lastListedForSaleDate",
        editor: { type: "date" },
        formatter: fmtDate,
        value: fmtDate(property?.lastListedForSaleDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "last listed date",
      },
      {
        label: "Last Listed Price",
        name: "lastListedForSalePrice",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.lastListedForSalePrice),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "last listed price",
      },
    ],
    [property],
  );
  return (
    <FieldsSection
      title="Listing"
      icon={<HomeOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const monthlyFromAnnual = (annual) => {
  if (annual === null || annual === undefined || annual === "") return null;
  const n = Number(annual);
  if (!Number.isFinite(n) || n === 0) return null;
  return fmtMoney(n / 12);
};

const FloodSection = ({ property, quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        label: "Flood Zone",
        name: "floodZone",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.floodZone),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "flood zone",
      },
      {
        label: "Flood Cert Date",
        name: "floodCertDate",
        editor: { type: "date" },
        formatter: fmtDate,
        value: fmtDate(property?.floodCertDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "flood cert date",
      },
      {
        label: "Annual Flood Insurance",
        name: "annualFloodInsurance",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.annualFloodInsurance),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "annual flood insurance",
      },
      {
        label: "Monthly Flood Insurance",
        value: monthlyFromAnnual(property?.annualFloodInsurance),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "monthly flood insurance",
        computedTooltip: "Annual Flood Insurance / 12",
      },
    ],
    [property],
  );
  return (
    <FieldsSection
      title="Flood"
      icon={<WaterOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const CostsSection = ({ property, quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        label: "Annual Taxes",
        name: "annualTaxes",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.annualTaxes),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "annual taxes",
      },
      {
        label: "Monthly Taxes",
        value: monthlyFromAnnual(property?.annualTaxes),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "monthly taxes",
        computedTooltip: "Annual Taxes / 12",
      },
      {
        label: "Annual Insurance",
        name: "annualInsurance",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.annualInsurance),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "annual insurance",
      },
      {
        label: "Monthly Insurance",
        value: monthlyFromAnnual(property?.annualInsurance),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "monthly insurance",
        computedTooltip: "Annual Insurance / 12",
      },
      {
        label: "Annual HOA Fees",
        name: "annualHoaFees",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.annualHoaFees),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "annual hoa fees",
      },
      {
        label: "Monthly HOA Fees",
        value: monthlyFromAnnual(property?.annualHoaFees),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "monthly hoa fees",
        computedTooltip: "Annual HOA Fees / 12",
      },
      {
        label: "Annual Property Mgmt Fees",
        name: "annualPropertyMgmtFees",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.annualPropertyMgmtFees),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "annual property management fees",
      },
      {
        label: "Monthly Property Mgmt Fees",
        value: monthlyFromAnnual(property?.annualPropertyMgmtFees),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "monthly property management fees",
        computedTooltip: "Annual Property Mgmt Fees / 12",
      },
      {
        label: "Annual Other Expenses",
        name: "annualOtherExpense",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.annualOtherExpense),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "annual other expenses",
      },
      {
        label: "Monthly Other Expenses",
        value: monthlyFromAnnual(property?.annualOtherExpense),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "monthly other expenses",
        computedTooltip: "Annual Other Expenses / 12",
      },
    ],
    [property],
  );
  return (
    <FieldsSection
      title="Carrying Costs"
      icon={<ReceiptLongOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

// Rows a non-construction, non-DSCR product (bridge, etc.) keeps.
const INTEREST_RESERVE_FIELDS = new Set(["interestReserves", "financedReserve"]);

const ConstructionBudgetSection = ({ property, quickFilter, reservesOnly }) => {
  // Options come from the subjectProperty model schema; the constant is only
  // the "schema not loaded yet" fallback (CLAUDE.md — never invent an option).
  const feasibilityStatusOptions = useModelFieldOptions(
    "feasibilityReportStatus",
    selectOptionsFeasibilityReportStatus,
    { objectType: "subjectProperty" },
  );
  const budget = Number(property?.constructionBudget);
  let pct = Number(property?.financedBudgetPercent);
  if (Number.isFinite(pct) && pct > 1) pct = pct / 100;
  const financedBudget =
    Number.isFinite(budget) && Number.isFinite(pct) && pct > 0
      ? budget * pct
      : Number.isFinite(budget)
        ? budget
        : null;

  const fields = useMemo(
    () => [
      {
        label: "Construction Budget",
        name: "constructionBudget",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.constructionBudget),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "construction budget",
      },
      {
        label: "Financed Budget %",
        name: "financedBudgetPercent",
        editor: { type: "percent" },
        formatter: (v) => fmtPercent(Number(v) > 1 ? Number(v) / 100 : v),
        value: fmtPercent(
          Number.isFinite(Number(property?.financedBudgetPercent)) &&
            Number(property?.financedBudgetPercent) > 1
            ? Number(property?.financedBudgetPercent) / 100
            : property?.financedBudgetPercent,
        ),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "financed budget percent holdback",
      },
      {
        label: "Financed Budget",
        value: fmtMoney(financedBudget),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "financed budget computed holdback",
        computedTooltip: "Construction Budget × Financed Budget %",
      },
      {
        label: "Interest Reserves",
        name: "interestReserves",
        editor: { type: "dollar" },
        formatter: fmtMoney,
        value: fmtMoney(property?.interestReserves),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "interest reserves",
      },
      {
        // Financed from loan proceeds → capitalized into UPB (feeds the loan's
        // capitalizedInterestReserve; validated against the Note / HUD).
        label: "Interest Reserve Financed",
        name: "financedReserve",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.financedReserve),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "interest reserve financed capitalized proceeds",
      },
      {
        label: "Budget Is Final",
        name: "budgetIsFinal",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.budgetIsFinal),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "budget is final",
      },
      {
        label: "Budget Matches Appraisal",
        name: "budgetMatchesAppraisal",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.budgetMatchesAppraisal),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "budget matches appraisal",
      },
      {
        label: "Final Budget Uploaded",
        name: "finalBudgetUploaded",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.finalBudgetUploaded),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "final budget uploaded",
      },
      {
        label: "Final Budget Feasible",
        name: "finalBudgetFeasible",
        editor: { type: "boolean" },
        formatter: fmtBool,
        value: fmtBool(property?.finalBudgetFeasible),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "final budget feasible feasibility",
      },
      {
        // The UW-maintained order/receipt stage of the third-party feasibility.
        // Separate from the computed `feasibility_required` below it: required
        // says whether one is expected, this says whether anyone ordered it.
        // Budget review reads it for the rollup's `ordered` flag.
        label: "Feasibility Report Status",
        name: "feasibilityReportStatus",
        editor: { type: "select", options: feasibilityStatusOptions },
        value: property?.feasibilityReportStatus,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "feasibility report status ordered received reviewed",
      },
      {
        label: "Feasibility Required",
        value: fmtBool(property?.feasibility_required),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "feasibility required",
        computedTooltip:
          "Computed by the LOS: budget over $150k, a change of use, an expansion, or a budget above the purchase price.",
      },
    ],
    [property, financedBudget, feasibilityStatusOptions],
  );
  return (
    <FieldsSection
      title={reservesOnly ? "Interest reserves" : "Construction & budget"}
      icon={<HomeWorkOutlined />}
      fields={
        reservesOnly
          ? fields.filter((f) => INTEREST_RESERVE_FIELDS.has(f.name))
          : fields
      }
      quickFilter={quickFilter}
    />
  );
};

const PropertyNotesSection = ({ property, quickFilter }) => {
  const notes = property?.subjectPropertyNotes;

  if (quickFilter && quickFilter.trim() !== "") {
    const q = quickFilter.toLowerCase();
    const hit =
      "notes subject property".includes(q) ||
      (notes || "").toLowerCase().includes(q);
    if (!hit) return null;
  }

  return (
    <PanelCard title="Notes" icon={<DescriptionOutlined />}>
      <Box sx={{ py: 1 }}>
        <Txt sx={{ fontSize: 11, color: grey[500], fontWeight: 500, mb: 0.5 }}>
          Subject property notes
        </Txt>
        <RffTextareaField
          name="subjectPropertyNotes"
          label={null}
          minHeight={100}
          suppressGrid
        />
      </Box>
    </PanelCard>
  );
};

/**
 * FieldsSection — a PanelCard whose body is a list of PanelRows built from a
 * `fields` config array (`{ label, value, fieldType, name, editor,
 * computedTooltip, blockedReason, onAdd, searchTerms }`).
 *
 * `fieldType` maps onto PanelRow's presentation:
 *   EDITABLE  → always-on inline RFF input (when `name` + `editor` given)
 *   COMPUTED  → filled value, or "blocked" when the value is absent
 *   READ_ONLY → locked value
 *
 * When `quickFilter` is set, rows that don't match are hidden and the card's
 * badge shows the visible/total count. The whole card disappears when nothing
 * matches.
 */
const FieldsSection = ({
  title,
  fields,
  icon,
  badge,
  quickFilter,
  defaultOpen = true,
  variant,
  disableCollapse,
}) => {
  const visibleFields = useMemo(
    () => fields.filter((f) => fieldPassesSearch(f, quickFilter)),
    [fields, quickFilter],
  );
  if (visibleFields.length === 0) return null;

  const isFiltered = visibleFields.length < fields.length;
  const resolvedBadge =
    badge ??
    (isFiltered ? `${visibleFields.length}/${fields.length}` : undefined);

  return (
    <PanelCard
      title={title}
      icon={icon}
      badge={resolvedBadge}
      defaultOpen={defaultOpen}
      // Matches the borrower sidebar and the Loan Info redesign tab — all three
      // render the same kind of card stack and should read as one system.
      variant={variant || "loanInfo"}
      disableCollapse={disableCollapse}
    >
      {visibleFields.map((f, i) => (
        <PanelRow
          key={f.label}
          label={f.label}
          name={f.name}
          editor={f.editor}
          value={f.value}
          editable={f.fieldType === FIELD_TYPE.EDITABLE}
          computed={f.fieldType === FIELD_TYPE.COMPUTED}
          computedTooltip={f.computedTooltip}
          blockedReason={f.blockedReason}
          state={f.state}
          onAdd={f.onAdd}
          searchTerms={f.searchTerms}
          quickFilter={quickFilter}
          isLast={i === visibleFields.length - 1}
        />
      ))}
    </PanelCard>
  );
};

/* ═══════════════════════════════════════════
   UNITS TAB — Unit Summary card + the in-place editable units table
   ═══════════════════════════════════════════ */
const PropertyUnitsTab = ({
  property,
  units,
  loanId,
  createRequested,
  onCreateHandled,
}) => (
  <ContentArea>
    <PanelCard
      disableCollapse
      title="Unit Summary"
      icon={<HomeWorkOutlined />}
    >
      <UnitSummaryRows units={units} />
    </PanelCard>

    {/* The table carries its own header (title + Add Unit). */}
    <Box sx={{ mt: 5, mb: 0.5 }}>
      <PropertyUnitsGrid
        units={units}
        loanId={loanId}
        propertyId={property?._id}
        createRequested={createRequested}
        onCreateHandled={onCreateHandled}
      />
    </Box>
  </ContentArea>
);

// Which Units-tab layout the sidebar renders:
//   "rentRoll" — KPI card + trimmed rent-roll table (PropertyUnitsSummaryTab)
//   "classic"  — Unit Summary rows + every-field table (PropertyUnitsGrid)
// Flip back to "classic" to revert; both stay in the codebase.
const UNITS_TAB_LAYOUT = "rentRoll";
const UnitsTabComponent =
  UNITS_TAB_LAYOUT === "rentRoll" ? PropertyUnitsSummaryTab : PropertyUnitsTab;

const UnitSummaryRows = ({ units = [] }) => {
  const summary = useMemo(() => {
    const total = units.length;
    const vacant = units.filter((u) => u?.unitVacant === true).length;
    const residential = units.filter((u) => u?.residentialUnit === true).length;
    const commercial = units.filter((u) => u?.commercialUnit === true).length;
    const sumRent = (key) =>
      units.reduce((acc, u) => {
        const n = Number(u?.[key]);
        return Number.isFinite(n) ? acc + n : acc;
      }, 0);
    return {
      total,
      vacant,
      residential,
      commercial,
      totalMarketRent: sumRent("unitMonthlyMarketRent"),
      totalInPlaceRent: sumRent("unitMonthlyInPlaceRent"),
    };
  }, [units]);

  const rows = [
    {
      label: "Total Units",
      value: summary.total || "0",
      tooltip: "Number of units",
    },
    {
      label: "Vacant Units",
      value: summary.vacant || "0",
      tooltip: "Count of units marked vacant",
    },
    {
      label: "Residential Units",
      value: summary.residential || "0",
      tooltip: "Count of units marked residential",
    },
    {
      label: "Commercial Units",
      value: summary.commercial || "0",
      tooltip: "Count of units marked commercial",
    },
    {
      label: "Total Market Rent",
      value: formatDollar(summary.totalMarketRent, "—"),
      tooltip: "Sum of each unit's Monthly Market Rent",
    },
    {
      label: "Total In-Place Rent",
      value: formatDollar(summary.totalInPlaceRent, "—"),
      tooltip: "Sum of each unit's Monthly In-Place Rent",
    },
  ];

  return (
    <>
      {rows.map((r, i) => (
        <PanelRow
          key={r.label}
          label={r.label}
          value={r.value}
          computed
          computedTooltip={r.tooltip}
          isLast={i === rows.length - 1}
        />
      ))}
    </>
  );
};

/* ═══════════════════════════════════════════
   FILES TAB
   ═══════════════════════════════════════════ */

// Doc groups offered when reassigning a property file's category inline —
// mirrors the allow-list the property upload process uses.
const PROPERTY_FILE_DOC_GROUPS = [
  "Appraisal",
  "Construction Documents",
  "Insurance Docs",
  "Leases & Rents",
  "Property Documents",
  "Title Docs",
];

/**
 * Documents tab, "assigned" layout — exactly the Assigned Documents rail from
 * the Properties-tab card (RecordDocCoveragePanel, same props), set in a white
 * card: doc-group coverage, Show missing, expand / collapse, the upload strip,
 * and per-type Upload / Assign Existing.
 */
const PropertyAssignedDocumentsTab = ({ files, property }) => (
  <ContentArea>
    <Box
      sx={{
        border: `1px solid ${slate[200]}`,
        borderRadius: "10px",
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <RecordDocCoveragePanel
        assignTo={{
          type: "property",
          id: property?._id,
          label:
            addressLabel(property?.address) ||
            property?.stringAddressFinalTitle ||
            "Property",
        }}
        files={files}
        linkedDocumentIds={property?.associations?.loanDocuments || []}
        masonry
      />
    </Box>
  </ContentArea>
);

const PropertyFilesTab = ({ files, property, loanId }) => {
  return (
    <ContentArea>
      <Flx column g={0} sx={{ pt: 1 }}>
        <PanelCard
          disableCollapse
          title="Upload"
          icon={<DescriptionOutlined />}
        >
          <Box sx={{ py: 0.5 }}>
            <LoanSubjectPropertyFileUploadProcess
              propertyId={property?._id}
              showDivider={false}
              loanId={loanId}
              perFileDocType
            />
          </Box>
        </PanelCard>
        <PanelCard
          disableCollapse
          title={`Property Associated files (${size(files)})`}
          icon={<HomeWorkOutlined />}
          bodySx={{ p: 0 }}
          headerEndContent={<AssignLoanFilesButton property={property} />}
        >
          <Box>
            {size(files) === 0 ? (
              <Flx center column sx={{ py: 4, color: grey[400], gap: 1 }}>
                <HomeWorkOutlined sx={{ fontSize: 28 }} />
                <Txt sx={{ fontSize: 13 }}>
                  No files linked to this property yet
                </Txt>
              </Flx>
            ) : (
              <InlineEditableLoanFilesTable
                files={files}
                loanId={loanId}
                metaField="docGroup"
                docGroupOptions={PROPERTY_FILE_DOC_GROUPS}
              />
            )}
          </Box>
        </PanelCard>
      </Flx>
    </ContentArea>
  );
};

const FilesTabComponent =
  FILES_TAB_LAYOUT === "assigned" ? PropertyAssignedDocumentsTab : PropertyFilesTab;

// Thin wrappers: guard null/undefined/"" → null, coerce to Number,
// then delegate the actual formatting to the shared project utilities.
const toFiniteNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtMoney = (v) => {
  const n = toFiniteNumber(v);
  return n === null ? null : formatDollar(n, null);
};

const fmtNumber = (v) => {
  const n = toFiniteNumber(v);
  return n === null ? null : formatNumber(n);
};

const fmtBool = (v) => formatBoolean(v);

const fmtDate = (v) => formatDateShared(v, null);

const fmtPercent = (v, opts) => {
  const n = toFiniteNumber(v);
  return n === null ? null : formatPercent(n, opts);
};

export default LoanSubjectPropertySidebar;
